<script setup>
import B2CContactPopup from 'components/home/B2CContactPopup.vue'
import { inject, ref } from 'vue'
import { apolloClient } from 'boot/apollo'
import { useMeta } from 'quasar'
import { useGlobalStore } from 'src/composables/useGlobalStore'
import referencesQuery from 'src/graphql/referencesQuery.gql'
import debounce from 'debounce'
import GeneralCard from 'components/general/GeneralCard.vue'
import B2CSearchBox from 'components/home/B2CSearchBox.vue'

const { globalConfiguration } = useGlobalStore()
const { isBrowser } = inject('Shared')

const references = ref([])
const page = ref(1)
const rowsPerPage = ref(12)

let loadReferences = async (resetItems, $state) => {
  try {
    if (!resetItems) {
      page.value += 1
    } else {
      page.value = 1
    }
    const referencesData = await apolloClient.query({
      query: referencesQuery,
      variables: {
        referencesInput: {
          rowsPerPage: rowsPerPage.value,
          page: page.value
        }
      }
    })
    const items = JSON.parse(JSON.stringify(referencesData?.data?.references))
    if (resetItems) {
      references.value = items
    } else {
      references.value = [...references.value, ...items]
      if (items.length < rowsPerPage.value) {
        $state.complete()
      } else {
        $state.loaded()
      }
    }
  } catch (e) {
    console.log('ERROR', e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}
await loadReferences(true)
loadReferences = debounce(loadReferences, 100)

if (globalConfiguration.value && globalConfiguration.value.pages && globalConfiguration.value.pages.references) {
  useMeta({
    title: globalConfiguration.value.pages.references.title,
    meta: {
      description: { name: 'description', content: globalConfiguration.value.pages.references.description },
      keywords: { name: 'keywords', content: globalConfiguration.value.pages.references.keyword },
      canonical: { rel: 'canonical', href: 'https://raumpioniere.ch/referenzen' },
      'og:title': { property: 'og:title', content: globalConfiguration.value.pages.references.ogTitle ? globalConfiguration.value.pages.references.ogTitle : globalConfiguration.value.pages.references.title },
      'og:image': { property: 'og:image', content: globalConfiguration.value.pages.references.ogImage ? globalConfiguration.value.pages.references.ogImage : 'https://raumpioniere.ch/icons/favicon-128x128.png' },
      'og:url': { property: 'og:url', content: 'https://raumpioniere.ch/referenzen' },
      'og:description': { property: 'og:description', content: globalConfiguration.value.pages.references.ogDescription ? globalConfiguration.value.pages.references.ogDescription : globalConfiguration.value.pages.references.description },
      'og:type': { property: 'og:type', content: globalConfiguration.value.pages.references.ogType ? globalConfiguration.value.pages.references.ogType : 'References' },
      'og:locale': { property: 'og:locale', content: 'de_CH' }
    }
  })
}

const loadData = async ($state) => {
  try {
    await loadReferences(false, $state)
  } catch (error) {
    $state.error()
  }
}
const contactVisible = ref(false)
</script>

<template lang="pug">
.references
  b2-c-contact-popup(v-model:visible="contactVisible" :always="true")
  .routerPageSize
    .references--header
      .references--header-title.gt-sm
        div Entwicklungsprojekte aus der
        div
          span Hand der
          strong &#32; Raumpioniere &#32;
      .references--header-title.lt-md
        | Entwicklungsprojekte aus der Hand der
        strong &#32; Raumpioniere &#32;
      .references--header-subtitle Unser Ziel ist es, für jede Ausgangslage die optimale Lösung zu finden und diese partnerschaftlich und transparent umzusetzen. Dabei entwickeln wir Ihre Liegenschaft bis zur rechtskräftigen Baubewilligung ohne finanzielles Risiko. In unseren Augen ist ein Projekt nur dann erfolgreich, wenn Eigentümer, Investoren, Gesellschaft und Landschaft gleichermassen davon profitieren. Mehr für Sie, mehr für die Schweiz!
    .references--body
      .references--body-items
        general-card.references--body--item(
          v-for="reference in references"
          :key="reference._id"
          :url="`/referenzen/${reference.url}`"
          :img="reference.teaserImg"
          :title="reference.teaserHeadline"
          :subtitle="reference.teaserContent"
          :categories="reference.categories"
        )
      .references--body--cta
        img.references--body--cta-img(src="~assets/img/aboutUs/Chris_round.png")
        .references--body--cta-title «Sie suchen nach der bestmöglichen Lösung für die Zukunft Ihrer Liegenschaft? Wir entwickeln Projekte für Sie und mit Ihnen. Transparent und partnerschaftlich.»
        .references--body--cta-name Christoph Giger
        .references--body--cta-company Raumpionier
        .references--body--cta-search-container
          b2-c-search-box(
            btnText="Jetzt Potenzial prüfen!"
            btnTextMobile="Jetzt prüfen!"
            inputColor="#2F3849"
            inputBg="white"
          )
      //infinite-loading(@infinite="loadData" v-if="isBrowser").infiniteLoading
      //  template(#complete)
      //    span
</template>

<style lang="sass">
.references
  .references--header
    @media screen and (min-width: 1024px)
      padding: 135px 0 77px
    @media screen and (max-width: 1024px)
      padding: 25px 0 34px
    .references--header-title
      font-weight: 500
      line-height: 130%
      text-align: center
      color: #22223C
      @media screen and (min-width: 1024px)
        font-size: 55px
        margin-bottom: 70px
      @media screen and (max-width: 1024px)
        font-size: 30px
        margin-bottom: 30px
    .references--header-subtitle
      line-height: 150%
      text-align: center
      color: #22223C
      @media screen and (min-width: 1024px)
        font-size: 20px
      @media screen and (max-width: 1024px)
        font-size: 14px
        padding: 0 10px
  .references--body
    @media screen and (min-width: 1024px)
      margin-bottom: 135px
    @media screen and (max-width: 1024px)
      margin-bottom: 60px
    .references--body-items
      display: grid
      width: 100%
      @media screen and (min-width: 1024px)
        grid-template-columns: repeat(3, minmax(0, 1fr))
        grid-gap: 40px
        margin-bottom: 132px
      @media screen and (max-width: 1024px) and (min-width: 600px)
        grid-template-columns: 1fr 1fr
        grid-gap: 35px
        margin-top: 32px
      @media screen and (max-width: 600px)
        margin-bottom: 50px
        grid-template-columns: 1fr
        grid-gap: 20px
        padding: 0 15px
    .references--body--cta
      display: flex
      flex-direction: column
      align-items: center
      .references--body--cta-img
        object-fit: cover
        border-radius: 100%
        margin-bottom: 40px
        @media screen and (min-width: 1024px)
          width: 213px
          height: 213px
        @media screen and (max-width: 1024px)
          width: 150px
          height: 150px
      .references--body--cta-title
        font-weight: 500
        line-height: 125%
        text-align: center
        background: linear-gradient(152.99deg, #2F3849 24.83%, #21252E 103.84%)
        -webkit-background-clip: text
        -webkit-text-fill-color: transparent
        background-clip: text
        text-fill-color: transparent
        margin-bottom: 20px
        max-width: 772px
        @media screen and (min-width: 1024px)
          font-size: 34px
        @media screen and (max-width: 1024px)
          font-size: 20px
      .references--body--cta-name
        font-weight: 700
        color: #1F47CD
        @media screen and (min-width: 1024px)
          font-size: 20px
        @media screen and (max-width: 1024px)
          font-size: 18px
      .references--body--cta-company
        background: linear-gradient(152.99deg, #2F3849 24.83%, #21252E 103.84%)
        -webkit-background-clip: text
        -webkit-text-fill-color: transparent
        background-clip: text
        text-fill-color: transparent
        @media screen and (min-width: 1024px)
          font-size: 18px
        @media screen and (max-width: 1024px)
          font-size: 16px
      .references--body--cta-search-container
        @media screen and (min-width: 1024px)
          margin-top: 80px
        @media screen and (max-width: 1024px)
          margin-top: 40px
    .infiniteLoading
      display: flex
      justify-content: center
      align-items: center
      margin: 30px 0 90px
      @media screen and (min-width: 1024px)
      @media screen and (max-width: 1024px)
</style>
